import { GameStateManager } from '@/components/GameState'
import { identifyTelegramUser, track, setUserProps, getTgContext } from '@/services/analytics'

import { CookieInteraction } from '@/components/CookieInteraction'
import { UIManager } from '@/components/UIManager'
import { Router } from '@/components/Router'
import { ReferralManager } from '@/components/ReferralManager'
import { LeaderboardManager } from '@/components/LeaderboardManager'
import { InventoryManager } from '@/components/InventoryManager'
import { MarketplaceManager } from '@/components/MarketplaceManager'
import { OnboardingManager } from '@/components/OnboardingManager'
import { PlayerProfileManager } from '@/components/PlayerProfileManager'
import { TonConnectManager } from '@/components/TonConnectManager'
import { SparklesManager } from '@/components/SparklesManager'
import { AnimationService } from '@/services/animations'
import { apiService } from '@/services/api'
import { RewardedAdManager } from '@/services/rewardedAds'
import { getTelegramUserId, getReferralCode, initializeTelegram } from '@/utils/telegram'
import { StorageService } from '@/utils/storage'
import { initDataManager } from '@/utils/initdata-refresh'

export class App {
  private gameState: GameStateManager
  private cookieInteraction: CookieInteraction
  private uiManager: UIManager
  private router: Router
  private referralManager: ReferralManager
  private leaderboardManager: LeaderboardManager
  private marketplaceManager: MarketplaceManager
  private inventoryManager: InventoryManager
  private onboardingManager: OnboardingManager
  private playerProfileManager: PlayerProfileManager
  private tonConnectManager: TonConnectManager
  private sparklesManager: SparklesManager
  private animations: AnimationService
  private rewardedAdManager: RewardedAdManager
  private userId: string

  constructor() {
    this.userId = getTelegramUserId()
    this.gameState = new GameStateManager()
    this.uiManager = new UIManager()
    this.router = new Router() // Sets up navigation automatically
    this.referralManager = new ReferralManager()
    this.leaderboardManager = new LeaderboardManager(this.userId)
    this.marketplaceManager = new MarketplaceManager(this.userId)
    this.inventoryManager = new InventoryManager(this.userId, {
      onBurn: async () => {
        await this.refreshState()
        await this.loadRewards()
      },
      onListingChange: async () => {
        await this.loadMarketplace()
      },
    })
    this.playerProfileManager = new PlayerProfileManager()
    this.tonConnectManager = new TonConnectManager()
    this.sparklesManager = new SparklesManager(this.userId)
    this.rewardedAdManager = new RewardedAdManager()

    const cookieSVG = document.getElementById('cookieSVG')! as unknown as SVGSVGElement
    this.cookieInteraction = new CookieInteraction(cookieSVG)
    this.animations = new AnimationService(cookieSVG)

    // Initialize onboarding manager
    this.onboardingManager = new OnboardingManager(
      () => this.onOnboardingComplete()
    )

    this.setupEventListeners()
    this.setupSVGFilters(cookieSVG)
  }

  private setupEventListeners(): void {
    // Subscribe to game state changes
    this.gameState.subscribe((state) => {
      this.uiManager.updateFromGameState(state, this.cookieInteraction.isCracked())
      this.cookieInteraction.updateFromGameState(state)
      this.referralManager.updateFromGameState(state)
    })

    // Buy button (default 1 pack)
    document.getElementById('buyCracks')!.addEventListener('click', () => {
      this.handlePurchase(1)
    })

    const boostShareBtn = document.getElementById('boostShareBtn')
    if (boostShareBtn) {
      boostShareBtn.addEventListener('click', () => {
        import('@/services/analytics').then(({ track }) => track('rewards_cta_clicked'))
        this.router.goTo('home')
        requestAnimationFrame(() => {
          document.getElementById('buyMoreBtn')?.dispatchEvent(new Event('click'))
        })
      })
    }

    // Rewarded ad button
    document.getElementById('watchRewardedAd')!.addEventListener('click', () => {
      this.handleRewardedAd()
    })

    // Listen for custom quantity purchases
    window.addEventListener('purchaseWithQuantity', (event: Event) => {
      const customEvent = event as CustomEvent
      this.handlePurchase(customEvent.detail.packs)
    })

    // Crack all button
    document.getElementById('crackAll')!.addEventListener('click', () => {
      const confirmed = confirm(
        "💰 'Crack All' will consume all your remaining energy and generate collectible fortune items for each energy!\n\n" +
        "This is a paid feature that gives you the same fortune items as manual cracking, but instantly.\n\n" +
        "Do you want to continue with 'Crack All'?"
      )
      
      if (confirmed) {
        this.handleFeaturePurchase("exhaust_today")
      }
    })

    // Listen for refresh events
    window.addEventListener('refreshState', () => {
      this.refreshState()
    })

    // Listen for cookie crack events
    window.addEventListener('cookieCracked', () => {
      this.handleCookieCrack()
    })

    window.addEventListener('resetCookieRequested', () => {
      this.resetCookie().catch(console.error)
    })

    // Listen for referral page shown events
    window.addEventListener('referralPageShown', () => {
      import('@/services/analytics').then(({ track }) => track('referrals_page_viewed'))
      this.updateReferralPage().catch(console.error)
    })

    // tasks tab loader
    window.addEventListener('tasksPageShown', (): void => {
      import('@/services/analytics').then(({ track }) => track('tasks_page_viewed'))
      this.loadTasks().catch(console.error)
    })

    // rewards page loader
    window.addEventListener('rewardsPageShown', (): void => {
      import('@/services/analytics').then(({ track }) => track('rewards_page_viewed'))
      this.loadRewards().catch(console.error)
      // Setup disconnect button when rewards page is shown
      this.tonConnectManager.setupDisconnectButton()
    })

    // leaderboard page loader
    window.addEventListener('leaderboardPageShown', (): void => {
      import('@/services/analytics').then(({ track }) => track('leaderboard_page_viewed'))
      this.loadLeaderboard().catch(console.error)
    })

    // inventory page loader
    window.addEventListener('inventoryPageShown', (): void => {
      import('@/services/analytics').then(({ track }) => track('inventory_page_viewed'))
      this.loadInventory().catch(console.error)
    })

    window.addEventListener('marketplacePageShown', (): void => {
      import('@/services/analytics').then(({ track }) => track('marketplace_page_viewed'))
      this.loadMarketplace().catch(console.error)
    })

    window.addEventListener('openPlayerProfile', (event: Event): void => {
      const detail = (event as CustomEvent<{
        id: string
        telegramId?: string | null
        display?: string
        username?: string
        rank?: number | null
        totalCracked?: number
      }>).detail

      if (!detail?.id) return

      this.router.goTo('profile')
      this.playerProfileManager.showProfile(detail.id, detail).catch(console.error)
    })

    // claim/verify/join (event delegation)
    document.addEventListener('click', (e: MouseEvent): void => {
      const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('#tasksList [data-action]')
      if (!btn) return

      const action = btn.getAttribute('data-action')
      if (!action) return

      // JOIN: open Telegram group and return (no taskId needed)
      if (action === 'join') {
        const url = btn.getAttribute('data-join-url') || 'https://t.me/xian_network'
        import('@/services/analytics').then(({ track }) => track('task_started', { method: 'join' }))
        if (window.Telegram?.WebApp?.openTelegramLink) {
          window.Telegram.WebApp.openTelegramLink(url)
        } else {
          window.open(url, '_blank')
        }
        return
      }

      // START: open Telegram bot and return (no taskId needed)
      if (action === 'start') {
        const url = btn.getAttribute('data-start-url') || 'https://t.me/xian_network'
        import('@/services/analytics').then(({ track }) => track('task_started', { method: 'start' }))
        if (window.Telegram?.WebApp?.openTelegramLink) {
          window.Telegram.WebApp.openTelegramLink(url)
        } else {
          window.open(url, '_blank')
        }
        return
      }

      // VOTE: open voting page and return (no taskId needed)
      if (action === 'vote') {
        const url = btn.getAttribute('data-vote-url') || 'https://t.me/tapps_bot/center?startapp=app_cookiecrackerp2e'
        import('@/services/analytics').then(({ track }) => track('task_started', { method: 'vote' }))
        // For t.me URLs, use Telegram WebApp openTelegramLink if available
        if (url.includes('t.me') && window.Telegram?.WebApp?.openTelegramLink) {
          window.Telegram.WebApp.openTelegramLink(url)
        } else {
          // Fallback to window.open for non-Telegram environments or other URLs
          window.open(url, '_blank')
        }
        return
      }

      // CLAIM / VERIFY need a taskId
      const taskId = btn.getAttribute('data-task-id')
      if (!taskId) return

        ; (async (): Promise<void> => {
          try {
            btn.disabled = true

            // Show loading state
            if (action === 'claim') {
              btn.textContent = 'Claiming...'
              btn.classList.add('loading')
              await apiService.claimTask(this.userId, taskId)
              import('@/services/analytics').then(({ track }) => track('task_completed', { task_id: taskId }))
            } else if (action === 'verify') {
              btn.textContent = 'Verifying...'
              btn.classList.add('loading')
              await apiService.verifyTask(this.userId, taskId)
              import('@/services/analytics').then(({ track }) => track('task_verified', { task_id: taskId }))
            }
            await this.refreshState()
            await this.loadTasks()
          } catch (err) {
            alert((err as Error).message)
          } finally {
            btn.disabled = false
            btn.classList.remove('loading')
            // Text will be updated when loadTasks() re-renders the tasks
          }
        })()
    })
  }

  private setupSVGFilters(svg: SVGSVGElement): void {
    let defs = svg.querySelector('defs')
    if (!defs) {
      defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs')
      svg.insertBefore(defs, svg.firstChild)
    }

    if (!svg.querySelector('#crumbShadow')) {
      const filter = document.createElementNS('http://www.w3.org/2000/svg', 'filter')
      filter.id = 'crumbShadow'

      const dropShadow = document.createElementNS('http://www.w3.org/2000/svg', 'feDropShadow')
      dropShadow.setAttribute('dx', '0')
      dropShadow.setAttribute('dy', '1')
      dropShadow.setAttribute('stdDeviation', '0.6')
      dropShadow.setAttribute('flood-opacity', '0.35')

      filter.appendChild(dropShadow)
      defs.appendChild(filter)
    }
  }


  private async loadTasks(): Promise<void> {
    const listEl = document.getElementById('tasksList')
    if (!listEl) return

    // Show loading skeleton with 10 items
    const skeletonItem = `
      <li class="task-row task-skeleton">
        <div class="t-main">
          <div class="t-title skeleton-text"></div>
          <div class="t-sub skeleton-text skeleton-text-small"></div>
        </div>
        <div class="t-cta">
          <div class="skeleton-button"></div>
        </div>
      </li>
    `
    listEl.innerHTML = Array(10).fill(skeletonItem).join('')

    try {
      const tasks = await apiService.getTasks(this.userId)
      this.uiManager.renderTasks(tasks)
    } catch (err) {
      listEl.innerHTML = `<li class="task-row"><div class="t-main"><div class="t-title">${(err as Error).message}</div></div></li>`
    }
  }

  private async loadRewards(): Promise<void> {
    // Show loading state
    const totalCrackedEl = document.getElementById('totalCrackedRewards')
    const estimatedRewardEl = document.getElementById('estimatedReward')
    if (totalCrackedEl) totalCrackedEl.textContent = '...'
    if (estimatedRewardEl) estimatedRewardEl.textContent = '...'

    try {
      const [tasks, state] = await Promise.all([
        apiService.getTasks(this.userId),
        apiService.getStatus(this.userId)
      ])
      this.uiManager.renderRewards(state, tasks)
    } catch (err) {
      console.error('Failed to load rewards:', err)
      // Reset to default values on error
      if (totalCrackedEl) totalCrackedEl.textContent = '0'
      if (estimatedRewardEl) estimatedRewardEl.textContent = '$0.00'
    }
  }

  private async loadLeaderboard(): Promise<void> {
    try {
      await this.leaderboardManager.loadLeaderboard()
    } catch (err) {
      console.error('Failed to load leaderboard:', err)
    }
  }

  private async loadInventory(): Promise<void> {
    try {
      await this.inventoryManager.loadInventory()
    } catch (err) {
      console.error('Failed to load inventory:', err)
    }
  }

  private async loadMarketplace(): Promise<void> {
    try {
      await this.marketplaceManager.loadMarketplace()
    } catch (err) {
      console.error('Failed to load marketplace:', err)
    }
  }

  private async attachReferralIfAny(): Promise<void> {
    const refCode = getReferralCode()
    if (!refCode) return

    if (StorageService.isReferralAttached(this.userId)) return

    try {
      await apiService.attachReferral(this.userId, refCode)
      StorageService.setReferralAttached(this.userId)
    } catch (error) {
      console.error('[referral] attach failed:', error)
    }
  }

  private async refreshState(): Promise<void> {
    try {
      const state = await apiService.getStatus(this.userId)
      this.gameState.setState(state)

      // If cookie was cracked and we're refreshing (e.g., after purchase), clear fortune
      if (this.cookieInteraction.isCracked()) {
        this.animations.hideFortune(document.getElementById('fortune')!)
        this.animations.hideFortuneActions(document.getElementById('fortuneActions')!)
        this.uiManager.clearFortune()
      }

      await this.cookieInteraction.reset()
    } catch (error) {
      console.error('Failed to refresh state:', error)
    }
  }

  private async resetCookie(): Promise<void> {
    this.animations.hideFortune(document.getElementById('fortune')!)
    this.animations.hideFortuneActions(document.getElementById('fortuneActions')!)
    await this.cookieInteraction.reset()
    this.uiManager.clearFortune()

    // Manually trigger UI update since game state didn't change but cookie state did
    const currentState = this.gameState.getState()
    this.uiManager.updateFromGameState(currentState, this.cookieInteraction.isCracked())
    this.cookieInteraction.updateFromGameState(currentState)
  }

  private crackInFlight = false;
  private async handleCookieCrack(): Promise<void> {
    if (this.crackInFlight) return;         // <-- guard
    this.crackInFlight = true;

    const before = this.gameState.getState()
    this.uiManager.updateFromGameState(before, true)

    try {
      const { fortune, state } = await apiService.getFortune(this.userId) // POST /fortune/:id

      const milestones = [1, 5, 10, 25, 50, 100, 250, 500, 1000]
      const isMilestone = milestones.includes(state.totalCracked)


      const shown = fortune

      this.uiManager.showFortune(shown)
      this.animations.showFortune(document.getElementById('fortune')!)
      this.animations.showFortuneActions(document.getElementById('fortuneActions')!)

      this.gameState.setState(state)

      const shownText = typeof shown === 'string' ? shown : shown.message
      import('@/services/analytics').then(({ track }) => {
        track('cookie_crack', {
          cracks_left_before: before.cracksLeft,
          cracks_left_after: state.cracksLeft,
          total_cracks: state.totalCracked,
          streak_count: state.streakCur,
          is_milestone: isMilestone,
          fortune_text_len: shownText.length,
        })
      })
    } catch (err) {
      alert((err as Error).message)
      await this.refreshState()
    }
    finally {
      this.crackInFlight = false;           // <-- release
    }
  }

  private async handlePurchase(quantity: number = 1): Promise<void> {
    const state = this.gameState.getState()
    if (state.cracksLeft > 0) return

    try {
      const totalCracks = quantity * 5
      const { invoice_url } = await apiService.createInvoice(this.userId, quantity)

      const source = quantity === 1 ? 'skip_wait_button' : 'buy_more_selector'
      import('@/services/analytics').then(({ track }) => track('purchase_started', {
        product_id: `cracks_pack_${totalCracks}`,
        source: source,
        quantity: quantity,
        total_cracks: totalCracks
      }))

      window.Telegram?.WebApp?.openInvoice?.(invoice_url, (status) => {
        if (status === 'paid') {
          import('@/services/analytics').then(({ track }) => {
            track('purchase_completed', {
              product_id: `cracks_pack_${totalCracks}`,
              quantity: quantity,
              total_cracks: totalCracks
            })
            track('cracks_refilled', { method: 'purchase', amount: totalCracks })
          })
          this.refreshState()
          const energyText = totalCracks === 1 ? 'energy' : 'energy'
          alert(`Thank you for your purchase! You now have ${totalCracks} more ${energyText}.`)
        }
      })
    } catch (error) {
      const totalCracks = quantity * 5
      import('@/services/analytics').then(({ track }) => track('purchase_failed', {
        product_id: `cracks_pack_${totalCracks}`,
        message: String((error as Error).message),
        quantity: quantity
      }))
      alert("Couldn't create invoice: " + (error as Error).message)
    }
  }

  private async handleFeaturePurchase(feature: string): Promise<void> {
    try {
      const { invoice_url } = await apiService.createFeatureInvoice(this.userId, feature)
      window.Telegram?.WebApp?.openInvoice?.(invoice_url, (status) => {
        if (status === 'paid') {
          this.refreshState()
          alert(`Thank you for purchasing!`)
        }
      })
    } catch (error) {
      alert("Couldn't create invoice: " + (error as Error).message)
    }
  }

  private async handleRewardedAd(): Promise<void> {
    const state = this.gameState.getState()
    
    // Check if user can watch rewarded ad
    if (state.cracksLeft > 0 || !state.canWatchRewardedAd) {
      return
    }

    // Disable the button to prevent multiple clicks
    const button = document.getElementById('watchRewardedAd') as HTMLButtonElement
    if (button) {
      button.disabled = true
      button.textContent = 'Loading Ad...'
    }

    try {
      const result = await this.rewardedAdManager.showRewardedAd()
      
      if (result.success) {
        // Ad was completed successfully - now call backend to give reward
        button.textContent = 'Processing reward...'
        
        try {
          // Call our backend to give the reward using the API service
          console.log(`[REWARD] Calling reward endpoint for user ${this.userId}`)
          console.log(`[REWARD] Current state before API call:`, {
            cracksLeft: this.gameState.getState().cracksLeft,
            canWatchRewardedAd: this.gameState.getState().canWatchRewardedAd
          })
          
          const responseText = await apiService.claimRewardedAdReward(this.userId)
          console.log(`[REWARD] Response: "${responseText}"`)
          
          // Immediately update local state to prevent button from showing again
          const currentState = this.gameState.getState()
          const updatedState = {
            ...currentState,
            cracksLeft: currentState.cracksLeft + 1,
            canWatchRewardedAd: false // User has now claimed today's reward
          }
          this.gameState.setState(updatedState)
          this.uiManager.updateFromGameState(updatedState, false)
          
          // Also refresh from backend to ensure consistency
          await new Promise(resolve => setTimeout(resolve, 500))
          console.log(`[REWARD] Refreshing state after reward...`)
          await this.refreshState()
          
          const newState = this.gameState.getState()
          console.log(`[REWARD] State after refresh:`, {
            cracksLeft: newState.cracksLeft,
            canWatchRewardedAd: newState.canWatchRewardedAd
          })
          
          // Hide the button immediately since reward was claimed
          if (button) {
            button.style.display = 'none'
            console.log('[REWARD] Hiding button after successful UI reward claim')
          }
          
          alert('🎉 You earned +1 energy! Keep cracking!')
          
          // Track the successful reward
          import('@/services/analytics').then(({ track }) => 
            track('rewarded_ad_completed', { 
              reward: 'energy',
              source: 'ui_call'
            })
          )
        } catch (rewardError) {
          console.error('Failed to process reward:', rewardError)
          console.error('Error type:', typeof rewardError)
          console.error('Error name:', (rewardError as Error).name)
          console.error('Error message:', (rewardError as Error).message)
          console.error('Error stack:', (rewardError as Error).stack)
          
          const errorMessage = (rewardError as Error).message
          
          // Handle specific error cases
          if (errorMessage === 'AD_REWARD_ALREADY_CLAIMED_TODAY' || 
              errorMessage.includes('AD_REWARD_ALREADY_CLAIMED') ||
              errorMessage.includes('already received your daily energy reward')) {
            
            // This is actually a success case! The ad provider (Adsgram) likely called our reward 
            // endpoint directly via server-to-server callback when the ad was completed.
            // This is a common pattern with ad networks to ensure rewards are granted reliably.
            console.log('[REWARD] Reward already claimed by ad provider - treating as success')
            
            // Update local state to reflect the reward was granted
            const currentState = this.gameState.getState()
            const updatedState = {
              ...currentState,
              cracksLeft: currentState.cracksLeft + 1,
              canWatchRewardedAd: false // User has now claimed today's reward
            }
            this.gameState.setState(updatedState)
            this.uiManager.updateFromGameState(updatedState, false)
            
            // Refresh from backend to get the actual state
            await new Promise(resolve => setTimeout(resolve, 500))
            console.log(`[REWARD] Refreshing state after ad provider reward...`)
            await this.refreshState()
            
            // Hide the button immediately since reward was claimed by ad provider
            if (button) {
              button.style.display = 'none'
              console.log('[REWARD] Hiding button after ad provider reward claim')
            }
            
            // Show success message
            alert('🎉 You earned +1 energy! Keep cracking!')
            
            // Track as successful reward (ad provider handled it)
            import('@/services/analytics').then(({ track }) => 
              track('rewarded_ad_completed', { 
                reward: 'energy',
                source: 'ad_provider_callback'
              })
            )
            
          } else if (errorMessage === 'USER_NOT_FOUND') {
            alert('User not found. Please try again.')
            
            // Refresh state to update UI
            await this.refreshState()
            
            // Track the error
            import('@/services/analytics').then(({ track }) => 
              track('rewarded_ad_reward_error', { 
                error: errorMessage,
                errorType: typeof rewardError,
                errorName: (rewardError as Error).name
              })
            )
          } else {
            alert(`Unable to give reward: ${errorMessage}. Please try again later.`)
            
            // Refresh state to update UI
            await this.refreshState()
            
            // Track the error
            import('@/services/analytics').then(({ track }) => 
              track('rewarded_ad_reward_error', { 
                error: errorMessage,
                errorType: typeof rewardError,
                errorName: (rewardError as Error).name
              })
            )
          }
        }
      } else {
        // Ad was not completed or failed
        console.warn('Rewarded ad failed:', result.error)
        alert(result.error || 'Ad could not be completed. Please try again later.')
        
        // Track the failure
        import('@/services/analytics').then(({ track }) => 
          track('rewarded_ad_failed', { error: result.error })
        )
      }
    } catch (error) {
      console.error('Rewarded ad error:', error)
      alert('Something went wrong. Please try again later.')
      
      // Track the error
      import('@/services/analytics').then(({ track }) => 
        track('rewarded_ad_error', { error: (error as Error).message })
      )
    } finally {
      // Reset button state, but only if the reward wasn't successfully claimed
      if (button) {
        const currentState = this.gameState.getState()
        
        console.log(`[REWARD] Finally block - button state:`, {
          canWatchRewardedAd: currentState.canWatchRewardedAd,
          cracksLeft: currentState.cracksLeft
        })
        
        if (currentState.canWatchRewardedAd) {
          // Reward wasn't claimed, so re-enable the button
          console.log('[REWARD] Re-enabling watch ad button')
          button.disabled = false
          button.innerHTML = 'Watch Ad for +1 energy'
          button.style.display = 'inline-flex'
          
          // Re-initialize lucide icons for the button
          if (typeof window !== 'undefined' && (window as any).lucide) {
            (window as any).lucide.createIcons()
          }
        } else {
          // Reward was claimed, hide the button
          console.log('[REWARD] Hiding watch ad button - reward already claimed today')
          button.style.display = 'none'
        }
      }
    }
  }

  private async updateReferralPage(): Promise<void> {
    // Update referral manager with current state when page is shown
    const currentState = this.gameState.getState()

    // If referral code is missing, try to refresh the state
    if (!currentState.refCode) {
      try {
        await this.refreshState()
        const newState = this.gameState.getState()
        this.referralManager.updateFromGameState(newState)
      } catch (error) {
        console.error('Failed to refresh state for referral page:', error)
        this.referralManager.updateFromGameState(currentState)
      }
    } else {
      this.referralManager.updateFromGameState(currentState)
    }
  }

  getCurrentRoute(): string {
    return this.router.getCurrentRoute()
  }

  private onOnboardingComplete(): void {
    // Called when onboarding is completed
    track('onboarding_flow_completed')
    // Refresh the UI to ensure everything is properly displayed
    this.refreshState()
  }



  async initialize(): Promise<void> {
    try {
      initializeTelegram()
      
      // Set up initData monitoring
      this.setupInitDataMonitoring()
      
      // Identify user and set context
      identifyTelegramUser(this.userId)
      setUserProps(getTgContext())
      // App opened / webapp ready
      track('app_opened', { ...getTgContext() })
      track('webapp_ready', { ...getTgContext() })
      await this.attachReferralIfAny()
      await this.refreshState()
      
      // Show onboarding for new users
      if (this.onboardingManager.shouldShow(this.userId)) {
        this.onboardingManager.show()
      }

      // Initialize sparkles manager
      await this.initializeSparklesManager()

      // Expose debug methods globally for development
      if (typeof window !== 'undefined') {
        (window as any).resetOnboarding = () => { OnboardingManager.resetOnboarding(this.userId) }
        (window as any).showOnboarding = () => { this.onboardingManager.show() }
        (window as any).checkInitData = () => {
          const age = initDataManager.getInitDataAge()
          const isStale = initDataManager.isInitDataStale()
          console.log('InitData status:', { age, isStale, userId: this.userId })
        }
        // Expose sparkles manager globally for modal interactions
        (window as any).sparklesManager = this.sparklesManager
      }
    } catch (error) {
      alert((error as Error).message)
    }
  }

  private async initializeSparklesManager(): Promise<void> {
    // Set up buy sparkles button
    const buySparklesBtn = document.getElementById('buySparklesBtn')
    if (buySparklesBtn) {
      buySparklesBtn.addEventListener('click', () => {
        this.sparklesManager.showPurchaseModal()
      })
    }

    // Initialize balance display
    await this.sparklesManager.refreshBalance()

    // Listen for balance changes to refresh marketplace
    window.addEventListener('sparklesBalanceChanged', () => {
      // Refresh marketplace if it's currently visible
      const marketplacePage = document.getElementById('page-marketplace')
      if (marketplacePage && marketplacePage.classList.contains('active')) {
        this.loadMarketplace()
      }
    })

    // Listen for balance refresh requests
    window.addEventListener('sparklesBalanceNeedsRefresh', () => {
      this.sparklesManager.refreshBalance()
    })
  }

  private setupInitDataMonitoring(): void {
    // Check initData age periodically and warn if getting stale
    setInterval(() => {
      if (initDataManager.isInitDataStale()) {
        console.warn('InitData is getting stale, consider refreshing soon')
      }
    }, 60000) // Check every minute

    // Register cleanup before refresh
    initDataManager.onBeforeRefresh(() => {
      console.log('App is about to refresh due to stale initData')
    })
  }
}
